/**
 * Service: §1 funnel analytics.
 *
 * Five reporting cuts, each a staff-only SECURITY DEFINER RPC. Scope is
 * enforced in Postgres (`analytics_assert_scope`): platform admins see
 * everything, a bank admin/approver sees only their own tenant.
 */
import { supabase } from "@/integrations/supabase/client";

export interface FunnelFilters {
  dateFrom?: Date | null;
  dateTo?: Date | null;
  programId?: string | null;
  tenantId?: string | null;
}

function args(f: FunnelFilters) {
  const to = f.dateTo ? new Date(f.dateTo) : null;
  if (to) to.setHours(23, 59, 59, 999);
  return {
    p_date_from: f.dateFrom ? f.dateFrom.toISOString() : null,
    p_date_to: to ? to.toISOString() : null,
    p_program_id: f.programId ?? null,
    p_tenant_id: f.tenantId ?? null,
  };
}

export interface OriginRow {
  origin: string;
  sessions: number;
  submitted: number;
  paid: number;
  revenue_cents: number;
  conversion_pct: number | null;
}

export interface CardTypeRow {
  key: string;
  count: number;
  style?: string;
  orientation?: string;
  approved?: number;
  rejected?: number;
  revenue_cents?: number;
}

export interface RegenRow {
  key: string;
  orders: number;
  generations: number;
  gens_per_order: number;
  regen_pct: number;
}

export interface TenantRow {
  tenant_id: string;
  tenant_name: string | null;
  brand_name: string | null;
  tenant_type: string | null;
  owned: number;
  routed_in: number;
  allocated: number;
  owned_paid: number;
  owned_revenue_cents: number;
}

export interface JourneyRow {
  state: string;
  seq: number;
  reached: number;
  stalled: number;
  median_secs: number | null;
}

export interface StatusRow {
  key: string;
  count: number;
  median_review_hours?: number | null;
}

export interface RevenueTierRow {
  tier_label: string;
  quantity: number;
  orders: number;
  revenue_cents: number;
  avg_order_cents: number;
}

export interface RevenueCut {
  captured_revenue_cents: number;
  captured_orders: number;
  avg_order_cents: number;
  authorised_not_captured_orders: number;
  authorised_not_captured_cents: number;
  by_tier: RevenueTierRow[];
  daily: { date: string; revenue_cents: number; orders: number }[];
}

export async function byOrigin(f: FunnelFilters) {
  return supabase.rpc("analytics_by_origin", args(f));
}

export async function byCardType(f: FunnelFilters) {
  return supabase.rpc("analytics_by_card_type", args(f));
}

export async function byTenant(f: FunnelFilters) {
  return supabase.rpc("analytics_by_tenant", args(f));
}

export async function byState(f: FunnelFilters) {
  return supabase.rpc("analytics_by_state", args(f));
}

export async function byRevenue(f: FunnelFilters) {
  return supabase.rpc("analytics_by_revenue", args(f));
}

export interface ProgramOption {
  id: string;
  name: string;
  slug: string;
}

export async function listProgramOptions(): Promise<{ data: ProgramOption[]; error: Error | null }> {
  const { data, error } = await supabase
    .from("programs")
    .select("id, name, slug")
    .order("name");
  if (error) return { data: [], error: error as unknown as Error };
  return { data: (data ?? []) as ProgramOption[], error: null };
}

/**
 * Conversion seam: stamps first-touch attribution, origin class, quantity and the
 * ordered tier onto a placed order. Advisory only — it never touches money.
 */
export async function bindCardRequestAttribution(args: {
  cardRequestId: string;
  guestToken: string | null;
  quantity?: number;
  tierLabel?: string | null;
}) {
  return supabase.rpc("bind_card_request_attribution", {
    p_token: args.guestToken ?? "",
    p_card_request_id: args.cardRequestId,
    p_quantity: args.quantity ?? 1,
    p_tier_label: args.tierLabel ?? null,
  });
}

/**
 * Fire-and-forget WITHOUT being silent.
 *
 * The bind must never be able to fail a payment, so it is not awaited by the
 * order path. It used to swallow its own errors, which made a failure invisible
 * by construction — and routing plus referral attribution both read what this
 * writes. So every failure is now logged AND recorded in
 * `attribution_bind_misses`, which `v_attribution_bind_miss_rate` turns into a
 * paging signal on a RATE (>=3 in 24h, or >10% of orders) rather than on one
 * transient blip.
 */
export function bindCardRequestAttributionMonitored(args: {
  cardRequestId: string;
  guestToken: string | null;
  quantity?: number;
  tierLabel?: string | null;
}): void {
  void (async () => {
    let reason: "rpc_error" | "transport_error" | "no_token" | null = null;
    let detail = "";
    try {
      // A missing guest token is NORMAL here: it is cleared at verification and
      // the order is placed afterwards. The RPC resolves the visit from the
      // verified account instead, so the call must still be made — and its own
      // answer, not the presence of a token, decides whether this is a miss.
      const { data, error } = await bindCardRequestAttribution(args);
      if (error) {
        reason = "rpc_error";
        detail = error.message ?? String(error);
      } else if (data && (data as any).ok === false) {
        reason = "no_token";
        detail = `no visit resolved: ${(data as any).reason ?? "unknown"}`;
      }
    } catch (e) {
      reason = "transport_error";
      detail = (e as Error)?.message ?? String(e);
    }
    if (!reason) return;
    console.error("[attribution] bind failed", {
      cardRequestId: args.cardRequestId,
      reason,
      detail,
    });
    try {
      await supabase.rpc("record_attribution_miss", {
        p_card_request_id: args.cardRequestId,
        p_reason: reason,
        p_detail: detail,
      });
    } catch (e) {
      // Last resort only: the ledger write itself is unreachable.
      console.error("[attribution] miss ledger unreachable", e);
    }
  })();
}



/* ------------------------------------------------- §2 step-by-step drop-off */

/**
 * DROP-OFF is measured session-by-session, and the DECREMENT is the point: an
 * even decline and a cliff are different diagnoses. `landed` is the denominator
 * so every row is comparable, and the table stops at "Application submitted" —
 * bank approval and fulfilment are issuer decisions, not customer behaviour,
 * and mixing them into one column would make a decrement mean two things.
 */
export interface DropoffRow {
  key: string;
  label: string;
  seq: number;
  kind: string;
  sessions: number;
  reached_pct: number | null;
  decrement_pct: number | null;
  dropped: number;
}

export interface IncidentRow {
  key: string;
  label: string;
  kind: string;
  seq: number;
  sessions: number;
  events: number;
}

export interface CohortRow {
  cohort_week: string;
  is_open: boolean;
  /**
   * THE WEEK ENDED BEFORE PER-SCREEN ARRIVALS WERE RECORDED.
   *
   * Those weeks only ever recorded `landed`, so every downstream percentage in
   * them is measured against an incomplete denominator — and that cannot be
   * repaired, because the past cannot be back-filled. The flag is derived in
   * `funnel_cohorts` from the earliest `arrived_*` event that exists, so it is
   * never a typed date and it stops being set once the window is fully covered.
   */
  is_pre_instrumentation: boolean;
  key: string;
  label: string;
  seq: number;
  kind: string;
  sessions: number;
  reached_pct: number | null;
  landed: number;
}

export interface DropoffFilters {
  dateFrom?: Date | null;
  dateTo?: Date | null;
  programId?: string | null;
  /** Our own accounts are hidden by DEFAULT; everything today is internal. */
  excludeInternal?: boolean;
}

function dropoffArgs(f: DropoffFilters) {
  const to = f.dateTo ? new Date(f.dateTo) : null;
  if (to) to.setHours(23, 59, 59, 999);
  return {
    p_date_from: f.dateFrom ? f.dateFrom.toISOString() : null,
    p_date_to: to ? to.toISOString() : null,
    p_program_id: f.programId ?? null,
    p_exclude_internal: f.excludeInternal !== false,
  };
}

export async function fetchDropoff(f: DropoffFilters = {}): Promise<DropoffRow[]> {
  const { data, error } = await supabase.rpc("funnel_dropoff", dropoffArgs(f));
  if (error) throw error;
  return (data ?? []) as DropoffRow[];
}

export async function fetchFunnelIncidents(f: DropoffFilters = {}): Promise<IncidentRow[]> {
  const { data, error } = await supabase.rpc("funnel_incidents", dropoffArgs(f));
  if (error) throw error;
  return (data ?? []) as IncidentRow[];
}

export async function fetchFunnelCohorts(
  weeks = 8,
  f: Pick<DropoffFilters, "programId" | "excludeInternal"> = {},
): Promise<CohortRow[]> {
  const { data, error } = await supabase.rpc("funnel_cohorts", {
    p_weeks: weeks,
    p_program_id: f.programId ?? null,
    p_exclude_internal: f.excludeInternal !== false,
  });
  if (error) throw error;
  return (data ?? []) as CohortRow[];
}
